import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  WarningOutlined,
  PictureOutlined,
  CreditCardOutlined,
  ExclamationCircleOutlined,
  RocketOutlined
} from '@ant-design/icons';
import { Card, Tag, Button, Alert, Checkbox, message } from 'antd';
import axios from 'axios';
import { useAuth } from '../../../../../contexts/AuthContext';

const MetaReviewLaunch = ({ campaignData }) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [preLaunchChecks, setPreLaunchChecks] = useState({
    paymentMethod: false,
    spendingLimit: false,
    specialCategory: campaignData.metaAds?.specialAdCategory === 'NONE'
  });
  const [isLaunching, setIsLaunching] = useState(false);
  const [launchError, setLaunchError] = useState(null);
  const [launchAttempted, setLaunchAttempted] = useState(false);

  // Calculate estimated results
  const calculateEstimates = () => {
    const baseReach = 10000;
    const baseImpressions = 25000;
    const baseFrequency = 2.5;

    // Adjust based on budget
    const budgetMultiplier = campaignData.metaAds?.adSetBudgetAmount ?
      Math.min(campaignData.metaAds.adSetBudgetAmount / 50, 5) : 1;

    // Adjust based on audience size (mock calculation)
    const audienceMultiplier = 1.2;

    // Adjust based on placements
    const placementCount = campaignData.metaAds?.selectedPlacements ?
      Object.values(campaignData.metaAds.selectedPlacements).flat().length : 1;
    const placementMultiplier = Math.min(placementCount / 3, 3);

    return {
      dailyReach: Math.round(baseReach * budgetMultiplier * audienceMultiplier * placementMultiplier),
      dailyImpressions: Math.round(baseImpressions * budgetMultiplier * audienceMultiplier * placementMultiplier),
      frequency: (baseFrequency * (budgetMultiplier * 0.5 + 1)).toFixed(1)
    };
  };

  const estimates = calculateEstimates();

  // Special category compliance is only required when a category other than NONE is chosen.
  // Read it live — campaignData can be filled in after this step mounts (single-page view).
  const needsSpecialCategoryCheck = (campaignData.metaAds?.specialAdCategory || 'NONE') !== 'NONE';

  const getValidationErrors = () => {
    // In single-page mode the per-step Continue validation can be skipped, so check required fields here
    const ads = campaignData.metaAds || {};
    return [
      !ads.campaignName?.trim() && 'Campaign Name is required',
      !ads.adSetName?.trim() && 'Ad Set Name is required',
      !ads.facebookPages?.length && 'Select at least one Facebook Page',
      !ads.selectedLocations?.length && 'Select at least one targeting location',
      ads.destinationType === 'website' && !ads.websiteUrl?.trim() && 'Website URL is required',
      !ads.primaryTexts?.length && 'Primary Text is required',
      !ads.headlines?.length && 'Headline is required',
      !preLaunchChecks.paymentMethod && 'Confirm your Payment Method in Pre-Launch Checks',
      needsSpecialCategoryCheck && !preLaunchChecks.specialCategory && 'Confirm Special Ad Category compliance in Pre-Launch Checks',
    ].filter(Boolean);
  };

  const handleLaunch = async () => {
    console.log('[MetaLaunch] Submit clicked. Pre-launch checks:', preLaunchChecks);
    setLaunchAttempted(true);

    const errors = getValidationErrors();
    if (errors.length) {
      console.warn('[MetaLaunch] Blocked: validation errors', errors);
      message.warning(errors[0]);
      return;
    }

    console.log('[MetaLaunch] Validation passed — building payload...');
    setIsLaunching(true);
    setLaunchError(null);

    try {
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
      const metaAds = campaignData.metaAds || {};

      const payload = {
        name: metaAds.campaignName || metaAds.name || 'Meta Campaign',
        description: `${(campaignData.campaignType || 'META').toUpperCase()} META Campaign`,
        status: 'pending',
        campaign_type: campaignData.campaignType || 'stop',
        ad_platform: 'meta',
        route_data: {
          locations: campaignData.selectedLocations || [],
          vehicles: campaignData.selectedVehicles || [],
          stops: campaignData.selectedStops || [],
          routes: campaignData.selectedRoutes || [],
        },
        meta_ads_config: {
          name: metaAds.campaignName || metaAds.name,
          objective: metaAds.objective,
          buying_type: metaAds.buyingType,
          special_ad_category: metaAds.specialAdCategory,
          advantage_plus_budget: metaAds.advantagePlusBudget,
          ad_set_name: metaAds.adSetName,
          performance_goal: metaAds.performanceGoal,
          ad_set_budget_type: metaAds.adSetBudgetType,
          ad_set_budget_amount: metaAds.adSetBudgetAmount,
          ad_set_currency: metaAds.adSetCurrency,
          ad_set_bid_strategy: metaAds.adSetBidStrategy,
          placement_type: metaAds.placementType,
          selected_placements: metaAds.selectedPlacements,
          ad_format: metaAds.adFormat,
          primary_texts: metaAds.primaryTexts,
          headlines: metaAds.headlines,
          call_to_action: metaAds.callToAction,
          destination_type: metaAds.destinationType,
          website_url: metaAds.websiteUrl,
          age_range: metaAds.ageRange,
          gender: metaAds.gender,
          selected_languages: metaAds.selectedLanguages,
          custom_audiences: metaAds.customAudiences,
          advantage_detailed_targeting: metaAds.advantageDetailedTargeting,
          ad_account_id: metaAds.adAccountId || null,
          account_slot: metaAds.accountSlot || null,
        },
      };

      console.log('[MetaLaunch] Calling POST /api/vehicle-campaigns with payload:', JSON.stringify(payload, null, 2));

      const response = await axios.post('/api/vehicle-campaigns', payload, { headers: authHeaders });

      console.log('[MetaLaunch] API response:', response.data);

      const fbPosted = response.data?.facebook_posted;
      const fbWarning = response.data?.facebook_warning;

      if (fbPosted && response.data?.facebook_activated) {
        const fbCampaignId = response.data?.meta_ads_config?.facebook_campaign_id;
        console.log('[MetaLaunch] ✅ Posted and activated on Facebook! Campaign ID:', fbCampaignId);
        // The backend switches the campaign, ad set and ad on, so there is no need to
        // open Ads Manager. Meta still reviews the ad before it starts delivering.
        message.success('Campaign is live! Meta will review the ad before it starts delivering (usually within 24 hours).', 5);
        setTimeout(() => navigate('/client/campaigns/new-camp-demo'), 2000);
      } else if (fbPosted) {
        const activationError = response.data?.facebook_activation_error || 'Unknown error';
        console.warn('[MetaLaunch] ⚠️ Created on Facebook but could not be activated:', activationError);
        setLaunchError(`The campaign was created on Facebook but is still paused: ${activationError}. You can turn it on with the resume button in your campaigns list.`);
        message.warning('Campaign created on Facebook but could not be switched on — see the details below.', 6);
      } else if (fbWarning) {
        console.warn('[MetaLaunch] ⚠️ Facebook posting failed:', fbWarning);
        setLaunchError(fbWarning);
        message.error('Campaign was not posted to Facebook — see the error below.', 6);
      } else {
        console.log('[MetaLaunch] Campaign saved in DB (no Facebook result in response)');
        message.success('Campaign saved!', 4);
        setTimeout(() => navigate('/client/campaigns/new-camp-demo'), 2000);
      }
    } catch (error) {
      const errMsg = error?.response?.data?.error || error?.message || 'Please try again.';
      console.error('[MetaLaunch] ❌ API call failed:', error?.response?.data || error?.message || error);
      setLaunchError(errMsg);
      message.error(`Failed to launch campaign: ${errMsg}`, 8);
    } finally {
      setIsLaunching(false);
    }
  };

  const renderCampaignBlock = () => (
    <Card title="Campaign" size="small">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <div>
          <div style={{ fontSize: '12px', color: 'var(--ink-200)', marginBottom: '4px' }}>Name</div>
          <div style={{ fontSize: '14px', fontWeight: 600 }}>{campaignData.metaAds?.campaignName || 'Untitled Campaign'}</div>
        </div>
        <div>
          <div style={{ fontSize: '12px', color: 'var(--ink-200)', marginBottom: '4px' }}>Objective</div>
          <div style={{ fontSize: '14px', fontWeight: 600 }}>
            {campaignData.metaAds?.objective ? campaignData.metaAds.objective.replace('_', ' ').toUpperCase() : 'Not set'}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '12px', color: 'var(--ink-200)', marginBottom: '4px' }}>Buying Type</div>
          <div style={{ fontSize: '14px', fontWeight: 600 }}>
            {campaignData.metaAds?.buyingType === 'AUCTION' ? 'Auction' : 'Reservation'}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '12px', color: 'var(--ink-200)', marginBottom: '4px' }}>Special Category</div>
          <div style={{ fontSize: '14px', fontWeight: 600 }}>
            {campaignData.metaAds?.specialAdCategory === 'NONE' ? 'None' : campaignData.metaAds?.specialAdCategory?.replace('_', ' ')}
          </div>
        </div>
        <div style={{ gridColumn: 'span 2' }}>
          <div style={{ fontSize: '12px', color: 'var(--ink-200)', marginBottom: '4px' }}>Advantage+ Budget</div>
          <Tag color={campaignData.metaAds?.advantagePlusBudget ? 'blue' : 'default'}>
            {campaignData.metaAds?.advantagePlusBudget ? 'Enabled' : 'Disabled'}
          </Tag>
        </div>
      </div>
    </Card>
  );

  const renderAdSetBlock = () => (
    <Card title="Ad Set" size="small">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <div>
          <div style={{ fontSize: '12px', color: 'var(--ink-200)', marginBottom: '4px' }}>Name</div>
          <div style={{ fontSize: '14px', fontWeight: 600 }}>{campaignData.metaAds?.adSetName || 'Untitled Ad Set'}</div>
        </div>
        <div>
          <div style={{ fontSize: '12px', color: 'var(--ink-200)', marginBottom: '4px' }}>Performance Goal</div>
          <div style={{ fontSize: '14px', fontWeight: 600 }}>
            {campaignData.metaAds?.performanceGoal ? campaignData.metaAds.performanceGoal.replace('_', ' ') : 'Not set'}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '12px', color: 'var(--ink-200)', marginBottom: '4px' }}>Attribution Setting</div>
          <div style={{ fontSize: '14px', fontWeight: 600 }}>
            {campaignData.metaAds?.attributionSetting ? campaignData.metaAds.attributionSetting.replace('_', ' ') : '7-day click'}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '12px', color: 'var(--ink-200)', marginBottom: '4px' }}>Frequency Control</div>
          <div style={{ fontSize: '14px', fontWeight: 600 }}>
            {campaignData.metaAds?.frequencyControl === 'TARGET' ? 'Target average' : 'Cap maximum'}
          </div>
        </div>
        <div style={{ gridColumn: 'span 2' }}>
          <div style={{ fontSize: '12px', color: 'var(--ink-200)', marginBottom: '4px' }}>Schedule</div>
          <div style={{ fontSize: '14px', fontWeight: 600 }}>
            {campaignData.metaAds?.adSetStartDate ? new Date(campaignData.metaAds.adSetStartDate).toLocaleDateString() : 'Immediate'}
            {campaignData.metaAds?.adSetEndDate && ` - ${new Date(campaignData.metaAds.adSetEndDate).toLocaleDateString()}`}
          </div>
        </div>
      </div>
    </Card>
  );

  const renderAudienceBlock = () => (
    <Card title="Audience" size="small">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <div>
          <div style={{ fontSize: '12px', color: 'var(--ink-200)', marginBottom: '4px' }}>Locations</div>
          <div style={{ fontSize: '14px', fontWeight: 600 }}>
            {campaignData.metaAds?.selectedLocations?.length || 0} locations
          </div>
        </div>
        <div>
          <div style={{ fontSize: '12px', color: 'var(--ink-200)', marginBottom: '4px' }}>Age Range</div>
          <div style={{ fontSize: '14px', fontWeight: 600 }}>
            {campaignData.metaAds?.ageRange ? `${campaignData.metaAds.ageRange[0]}-${campaignData.metaAds.ageRange[1]}` : '18-65+'}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '12px', color: 'var(--ink-200)', marginBottom: '4px' }}>Gender</div>
          <div style={{ fontSize: '14px', fontWeight: 600 }}>
            {campaignData.metaAds?.gender === 'all' ? 'All' : campaignData.metaAds?.gender === 'men' ? 'Men' : 'Women'}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '12px', color: 'var(--ink-200)', marginBottom: '4px' }}>Languages</div>
          <div style={{ fontSize: '14px', fontWeight: 600 }}>
            {campaignData.metaAds?.selectedLanguages?.length || 0} languages
          </div>
        </div>
        <div>
          <div style={{ fontSize: '12px', color: 'var(--ink-200)', marginBottom: '4px' }}>Custom Audiences</div>
          <div style={{ fontSize: '14px', fontWeight: 600 }}>
            {campaignData.metaAds?.customAudiences?.length || 0} audiences
          </div>
        </div>
        <div>
          <div style={{ fontSize: '12px', color: 'var(--ink-200)', marginBottom: '4px' }}>Detailed Targeting</div>
          <Tag color={campaignData.metaAds?.advantageDetailedTargeting ? 'blue' : 'default'}>
            {campaignData.metaAds?.advantageDetailedTargeting ? 'Advantage+ AI' : 'Manual'}
          </Tag>
        </div>
      </div>
    </Card>
  );

  const renderPlacementsBlock = () => (
    <Card title="Placements" size="small">
      <div style={{ marginBottom: '12px' }}>
        <div style={{ fontSize: '12px', color: 'var(--ink-200)', marginBottom: '4px' }}>Type</div>
        <Tag color={campaignData.metaAds?.placementType === 'advantage' ? 'blue' : 'green'}>
          {campaignData.metaAds?.placementType === 'advantage' ? 'Advantage+ Placements' : 'Manual Placements'}
        </Tag>
      </div>

      {campaignData.metaAds?.placementType === 'manual' && (
        <div>
          <div style={{ fontSize: '12px', color: 'var(--ink-200)', marginBottom: '8px' }}>Platforms</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {campaignData.metaAds?.selectedPlacements?.facebook?.length > 0 && <Tag color="blue">Facebook ({campaignData.metaAds.selectedPlacements.facebook.length})</Tag>}
            {campaignData.metaAds?.selectedPlacements?.instagram?.length > 0 && <Tag color="pink">Instagram ({campaignData.metaAds.selectedPlacements.instagram.length})</Tag>}
            {campaignData.metaAds?.selectedPlacements?.audienceNetwork?.length > 0 && <Tag color="purple">Audience Network ({campaignData.metaAds.selectedPlacements.audienceNetwork.length})</Tag>}
            {campaignData.metaAds?.selectedPlacements?.messenger?.length > 0 && <Tag color="cyan">Messenger ({campaignData.metaAds.selectedPlacements.messenger.length})</Tag>}
          </div>
        </div>
      )}
    </Card>
  );

  const renderBudgetBlock = () => (
    <Card title="Budget" size="small">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <div>
          <div style={{ fontSize: '12px', color: 'var(--ink-200)', marginBottom: '4px' }}>Type</div>
          <div style={{ fontSize: '14px', fontWeight: 600 }}>
            {campaignData.metaAds?.adSetBudgetType === 'DAILY' ? 'Daily Budget' : 'Lifetime Budget'}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '12px', color: 'var(--ink-200)', marginBottom: '4px' }}>Amount</div>
          <div style={{ fontSize: '14px', fontWeight: 600 }}>
            {campaignData.metaAds?.adSetCurrency || 'USD'} {campaignData.metaAds?.adSetBudgetAmount || '0'}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '12px', color: 'var(--ink-200)', marginBottom: '4px' }}>Bid Strategy</div>
          <div style={{ fontSize: '14px', fontWeight: 600 }}>
            {campaignData.metaAds?.adSetBidStrategy ? campaignData.metaAds.adSetBidStrategy.replace('_', ' ') : 'Highest Volume'}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '12px', color: 'var(--ink-200)', marginBottom: '4px' }}>Schedule</div>
          <Tag color={campaignData.metaAds?.adSetBudgetSchedulingEnabled ? 'blue' : 'default'}>
            {campaignData.metaAds?.adSetBudgetSchedulingEnabled ? 'Time Slots Added' : 'No Scheduling'}
          </Tag>
        </div>
      </div>
    </Card>
  );

  const renderCreativeBlock = () => (
    <Card title="Creative" size="small">
      <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '16px' }}>
        <div>
          <div style={{ fontSize: '12px', color: 'var(--ink-200)', marginBottom: '4px' }}>Format</div>
          <div style={{
            width: '100px',
            height: '60px',
            background: '#f3f4f6',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
          }}>
            <PictureOutlined />
          </div>
          <div style={{ fontSize: '12px', fontWeight: 600, marginTop: '4px' }}>
            {campaignData.metaAds?.adFormat ? campaignData.metaAds.adFormat.toUpperCase() : 'SINGLE'}
          </div>
        </div>

        <div>
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '12px', color: 'var(--ink-200)', marginBottom: '4px' }}>Primary Text</div>
            <div style={{ fontSize: '14px', fontWeight: 600, lineHeight: 1.4 }}>
              {campaignData.metaAds?.primaryTexts?.[0] || 'No primary text set'}
            </div>
          </div>

          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '12px', color: 'var(--ink-200)', marginBottom: '4px' }}>Headline</div>
            <div style={{ fontSize: '14px', fontWeight: 600 }}>
              {campaignData.metaAds?.headlines?.[0] || 'No headline set'}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <div style={{ fontSize: '12px', color: 'var(--ink-200)', marginBottom: '4px' }}>CTA</div>
              <div style={{ fontSize: '14px', fontWeight: 600 }}>
                {campaignData.metaAds?.callToAction ? campaignData.metaAds.callToAction.replace('_', ' ').toUpperCase() : 'LEARN MORE'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: 'var(--ink-200)', marginBottom: '4px' }}>Destination</div>
              <div style={{ fontSize: '14px', fontWeight: 600 }}>
                {campaignData.metaAds?.destinationType === 'website' ? 'Website' : campaignData.metaAds?.destinationType || 'Not set'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );

  const renderPreLaunchChecks = () => (
    <Card title="Pre-Launch Checks" size="small">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Checkbox
            checked={preLaunchChecks.paymentMethod}
            onChange={(e) => setPreLaunchChecks({...preLaunchChecks, paymentMethod: e.target.checked})}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <CreditCardOutlined style={{ color: 'var(--ink-200)' }} />
            <div>
              <div style={{ fontSize: '14px', fontWeight: 600 }}>Payment Method <span style={{ color: '#ef4444' }}>*</span></div>
              <div style={{ fontSize: '12px', color: 'var(--ink-200)' }}>Confirm your payment method is set up and active</div>
              {launchAttempted && !preLaunchChecks.paymentMethod && (
                <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '2px' }}>Please confirm your payment method</div>
              )}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Checkbox
            checked={preLaunchChecks.spendingLimit}
            onChange={(e) => setPreLaunchChecks({...preLaunchChecks, spendingLimit: e.target.checked})}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <WarningOutlined style={{ color: '#f59e0b' }} />
            <div>
              <div style={{ fontSize: '14px', fontWeight: 600 }}>Account Spending Limit</div>
              <div style={{ fontSize: '12px', color: 'var(--ink-200)' }}>Check your account spending limit (if applicable)</div>
            </div>
          </div>
        </div>

        {needsSpecialCategoryCheck && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Checkbox
              checked={preLaunchChecks.specialCategory}
              onChange={(e) => setPreLaunchChecks({...preLaunchChecks, specialCategory: e.target.checked})}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ExclamationCircleOutlined style={{ color: '#ef4444' }} />
              <div>
                <div style={{ fontSize: '14px', fontWeight: 600 }}>Special Ad Category <span style={{ color: '#ef4444' }}>*</span></div>
                <div style={{ fontSize: '12px', color: 'var(--ink-200)' }}>
                  Confirm compliance with {campaignData.metaAds?.specialAdCategory?.replace('_', ' ')} requirements
                </div>
                {launchAttempted && !preLaunchChecks.specialCategory && (
                  <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '2px' }}>Please confirm compliance</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );

  const renderEstimatedResults = () => (
    <Card title="Estimated Results" size="small">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: 700, color: 'var(--ink)', marginBottom: '4px' }}>
            {estimates.dailyReach.toLocaleString()}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--ink-200)' }}>Estimated Reach / day</div>
        </div>

        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: 700, color: '#10b981', marginBottom: '4px' }}>
            {estimates.dailyImpressions.toLocaleString()}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--ink-200)' }}>Estimated Impressions / day</div>
        </div>

        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: 700, color: '#f59e0b', marginBottom: '4px' }}>
            {estimates.frequency}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--ink-200)' }}>Estimated Frequency</div>
        </div>
      </div>

      <Alert
        message="Estimates are based on similar campaigns and current market conditions"
        type="info"
        showIcon
        style={{ marginTop: '16px' }}
      />
    </Card>
  );

  return (
    <div className="step-container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px 120px' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <div style={{
          width: '64px',
          height: '64px',
          borderRadius: '16px',
          background: '#f3f4f6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '24px',
        }}>
          <RocketOutlined style={{ fontSize: '32px', color: '#111827' }} />
        </div>

        <h1 style={{
          fontSize: '32px',
          fontWeight: 700,
          color: '#000',
          marginBottom: '12px',
          textAlign: 'center'
        }}>
          Review & Launch
        </h1>
        <p style={{
          fontSize: '16px',
          color: 'var(--ink-200)',
          marginBottom: 0,
          textAlign: 'center'
        }}>
          Review your campaign settings and launch when ready
        </p>
      </div>

      {launchError && (
        <Alert
          type="error"
          showIcon
          closable
          onClose={() => setLaunchError(null)}
          message="Campaign is not live on Facebook"
          description={launchError}
          style={{ marginBottom: '24px' }}
        />
      )}

      {/* Campaign Summary Cards — 3 per row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '32px' }}>
        {renderCampaignBlock()}
        {renderAdSetBlock()}
        {renderAudienceBlock()}
        {renderPlacementsBlock()}
        {renderBudgetBlock()}
        {renderCreativeBlock()}
      </div>

      {/* Pre-Launch Checks + Estimated Results side by side */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '32px', alignItems: 'start' }}>
        {renderPreLaunchChecks()}
        {renderEstimatedResults()}
      </div>

      {/* Shown after a Submit attempt; updates live as the user fixes each item */}
      {launchAttempted && getValidationErrors().length > 0 && (
        <Alert
          type="error"
          showIcon
          message="Please fix the following before submitting"
          description={
            <ul style={{ margin: 0, paddingLeft: '18px' }}>
              {getValidationErrors().map(err => <li key={err}>{err}</li>)}
            </ul>
          }
          style={{ marginBottom: '24px' }}
        />
      )}

      {/* Launch Section */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: '#fff',
        borderTop: '1px solid var(--paper-300)',
        padding: '16px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        zIndex: 10
      }}>
        <div style={{ fontSize: '14px', color: 'var(--ink-200)' }}>
          Building: Campaign Review & Launch
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ fontSize: '12px', color: 'var(--ink-200)', fontStyle: 'italic' }}>
            *Most ads are reviewed within 24 hours
          </div>

          {/* Always clickable — handleLaunch shows what is missing instead of silently doing nothing */}
          <Button
            type="primary"
            size="large"
            loading={isLaunching}
            onClick={handleLaunch}
            title="Submit campaign to Facebook Ads Manager"
            style={{ padding: '12px 32px', fontSize: '16px', fontWeight: 600, background: 'var(--ink)', borderColor: 'var(--ink)', color: '#fff' }}
          >
            {isLaunching ? 'Submitting...' : 'Submit for Review'}
            {!isLaunching && <RocketOutlined style={{ marginLeft: '8px' }} />}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MetaReviewLaunch;
